import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BadgeComponent } from '@sb/ui/badge';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { InputModule } from '@sb/ui/input';
import { RadioComponent } from '@sb/ui/radio';
import { RadioGroupComponent } from '@sb/ui/radio-group';
import { SelectComponent } from '@sb/ui/select';
import { clsx } from 'clsx';

import { fieldTypeOptions } from '../../../field-type-options.helper';
import { hasVariableName, variableNameFromLabel } from '../../../variable-name.helper';
import {
  entryKey,
  isCardAnsweredForYou,
  type BlankCard,
  type BlankDecision,
  type BlankEntry,
} from '../../detection-derivations.helper';
import { entryOptions, selectedOptionId, type EntryOption, type EntryOptionId } from './entry-options.helper';

export interface EntryDecision {
  entryKey: string;
  decision: BlankDecision;
}

type NewFieldDecision = Extract<BlankDecision, { kind: 'create_new' }>;

interface EntryRow {
  entry: BlankEntry;
  key: string;
  options: EntryOption[];
  selected: EntryOptionId | null;
  /** The field being created, once the customer picked to create one. */
  newField: NewFieldDecision | null;
  /** The new field's placeholder as it lands in the document. */
  variable: string | null;
  unnamed: boolean;
}

/** One detected spot, or a group of new proposals that share a paragraph, with its answer. */
@Component({
  selector: 'field-card',
  templateUrl: './field-card.component.html',
  imports: [
    BadgeComponent,
    FormsModule,
    InputModule,
    RadioComponent,
    RadioGroupComponent,
    SbFormFieldComponent,
    SelectComponent,
    TranslatePipe,
  ],
  host: {
    role: 'group',
    '[attr.aria-label]': 'card().title',
    '[class]': 'hostClasses()',
  },
})
export class FieldCardComponent {
  #translate = inject(TranslateService);

  card = input.required<BlankCard>();
  decisions = input.required<ReadonlyMap<string, BlankDecision>>();
  decided = output<EntryDecision[]>();

  typeOptions = fieldTypeOptions(this.#translate).filter(({ value }) => value !== 'select');
  lockedTypeOptions = fieldTypeOptions(this.#translate).filter(({ value }) => value === 'select');

  rows = computed<EntryRow[]>(() =>
    this.card().entries.map((entry) => {
      const options = entryOptions(entry);
      const selected = selectedOptionId(entry, this.decisions());
      const decision = this.decisions().get(entryKey(entry)) ?? options.find(({ id }) => id === selected)?.decision;
      const newField = decision?.kind === 'create_new' ? decision : null;

      return {
        entry,
        key: entryKey(entry),
        options,
        selected,
        newField,
        variable: newField ? `{${newField.name}}` : null,
        unnamed: newField !== null && !hasVariableName(newField.name),
      };
    }),
  );

  isGroup = computed(() => this.rows().length > 1);
  isMatched = computed(() => isCardAnsweredForYou(this.card()));

  hostClasses = computed(() =>
    clsx(
      'flex flex-col gap-md rounded-xl border border-solid bg-surface-page p-lg',
      this.isMatched() ? 'border-default' : 'border-warning-subtle',
    ),
  );

  choose(row: EntryRow, optionId: unknown): void {
    const option = row.options.find((candidate) => candidate.id === optionId);

    if (option && option.id !== row.selected) {
      this.decided.emit([{ entryKey: row.key, decision: option.decision }]);
    }
  }

  rename(row: EntryRow, newField: NewFieldDecision, label: string): void {
    this.decided.emit([{ entryKey: row.key, decision: { ...newField, label, name: variableNameFromLabel(label) } }]);
  }

  retype(row: EntryRow, newField: NewFieldDecision, fieldType: unknown): void {
    const option = this.typeOptions.find(({ value }) => value === fieldType);

    if (option) {
      this.decided.emit([{ entryKey: row.key, decision: { ...newField, fieldType: option.value } }]);
    }
  }
}
